import { useEffect, useState } from 'react'
import { fetchPortfolioInbox, savePortfolioMemory } from '../../api/portfolio'
import type { PortfolioInboxItem } from './types'

function formatWhen(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

interface PortfolioInboxProps {
  refreshTick?: number
}

export function PortfolioInbox({ refreshTick = 0 }: PortfolioInboxProps) {
  const [items, setItems] = useState<PortfolioInboxItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [savedId, setSavedId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchPortfolioInbox(100)
      setItems(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inbox')
    } finally {
      setLoading(false)
    }
  }

  const saveItem = async (item: PortfolioInboxItem) => {
    setSavingId(item.id)
    setSavedId(null)
    try {
      await savePortfolioMemory({
        title: item.subject,
        content: item.body,
        sourceType: item.source,
        sourceId: item.id,
        tags: [item.from, item.to || '', item.type, item.priority || '', item.status || ''].filter(Boolean),
        createdBy: 'portfolio-inbox',
      })
      setSavedId(item.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save memory')
    } finally {
      setSavingId(null)
    }
  }

  useEffect(() => {
    load()
  }, [refreshTick])

  if (loading) {
    return <div className="text-swiss-small text-swiss-gray-500">Loading inbox...</div>
  }

  if (error) {
    return (
      <div className="border border-swiss-red bg-swiss-off-white p-4">
        <p className="text-swiss-small text-swiss-red mb-2">{error}</p>
        <button onClick={load} className="text-swiss-small underline underline-offset-2 cursor-pointer">
          Retry
        </button>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="border border-swiss-gray-200 bg-swiss-off-white p-6">
        <h2 className="text-swiss-label uppercase tracking-widest text-swiss-gray-500 mb-3">
          Unified Inbox
        </h2>
        <p className="text-swiss-small text-swiss-gray-500">No dispatch messages or work orders yet.</p>
      </div>
    )
  }

  return (
    <div className="border border-swiss-gray-200 bg-swiss-off-white p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-swiss-label uppercase tracking-widest text-swiss-gray-500">
          Unified Inbox
        </h2>
        <button onClick={load} className="text-swiss-caption text-swiss-gray-500 underline underline-offset-2 cursor-pointer">
          Refresh
        </button>
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="border border-swiss-gray-200 bg-swiss-white p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-swiss-small text-swiss-black">{item.subject}</p>
              <span className="text-swiss-caption font-swiss-mono text-swiss-gray-400">
                {formatWhen(item.createdAt)}
              </span>
            </div>
            <p className="text-swiss-caption text-swiss-gray-500 mt-1">
              {item.source} · {item.from}{item.to ? ` -> ${item.to}` : ''}{item.status ? ` · ${item.status}` : ''}
            </p>
            <p className="text-swiss-small text-swiss-gray-700 mt-2 whitespace-pre-wrap">{item.body}</p>
            <div className="mt-2 flex items-center justify-end gap-3">
              {savedId === item.id && (
                <span className="text-swiss-caption text-swiss-gray-500">Saved</span>
              )}
              <button
                onClick={() => saveItem(item)}
                disabled={savingId === item.id}
                className="text-swiss-caption underline underline-offset-2 cursor-pointer disabled:opacity-50"
              >
                {savingId === item.id ? 'Saving...' : 'Save to Memory'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
